import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { DeletedUserBadge } from "@/components/DeletedUserBadge";
import { toast } from "@/components/Toast";
import { useAuth } from "@/contexts/AuthContext";
import { ArrowLeft, ClipboardList, Plus, Trash2, X, BarChart3, CheckCircle2, XCircle, Pencil } from "lucide-react";
import { ROLE_LABELS as ROLE_LABEL, USER_ROLE_KEYS } from "@/lib/roles";

export default function Quizzes() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const backLink = isAdmin ? "/admin" : "/supervisor";
  const [quizzes, setQuizzes] = useState([]);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [auditFor, setAuditFor] = useState(null);

  const load = async () => {
    try { const r = await api.get("/quizzes"); setQuizzes(r.data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar cuestionario?")) return;
    try { await api.delete(`/quizzes/${id}`); toast.success("Eliminado"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div style={{ padding: "20px 32px", maxWidth: 1100, margin: "0 auto" }} data-testid="quizzes-page">
      <Link to={backLink} style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "var(--invict-text-muted)", fontSize: 12, marginBottom: 12, textDecoration: "none", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em" }}><ArrowLeft size={14}/> Volver</Link>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10, marginBottom: 16 }}>
        <div>
          <h1 style={{ fontSize: 26, fontWeight: 900, margin: 0, color: "#0A0A0A", letterSpacing: "-0.02em" }}>Cuestionarios obligatorios</h1>
          <div style={{ fontSize: 12, color: "var(--invict-text-muted)" }}>Evaluación de conocimiento con bloqueo de interfaz al iniciar sesión</div>
        </div>
        {isAdmin && <button onClick={() => setCreating(true)} className="btn-primary" style={{ padding: "10px 16px" }} data-testid="quiz-add"><Plus size={14} style={{ marginRight: 6 }}/>Nuevo cuestionario</button>}
      </div>

      <div className="card" style={{ padding: 0, overflow: "hidden" }} data-testid="quiz-list">
        {quizzes.length === 0 && <div style={{ padding: 30, textAlign: "center", color: "var(--invict-text-muted)" }}><ClipboardList size={28} style={{ opacity: 0.4, margin: "0 auto 6px" }}/><div style={{ fontSize: 13 }}>Sin cuestionarios creados.</div></div>}
        {quizzes.map((q) => (
          <div key={q.id} style={{ padding: "14px 16px", borderBottom: "1px solid var(--invict-bone-3)", display: "flex", gap: 12, alignItems: "center" }} data-testid={`quiz-row-${q.id}`}>
            <ClipboardList size={22} style={{ color: "#BF3437", flexShrink: 0 }}/>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 900, fontSize: 14, color: "#0A0A0A" }}>{q.title}</div>
              <div style={{ fontSize: 11, color: "var(--invict-text-muted)", marginTop: 2 }}>{q.questions.length} preguntas · mín. {q.pass_score}% · cada {q.period_days} días · {(q.assign_roles || []).map((r) => ROLE_LABEL[r] || r).join(", ")}</div>
              <div style={{ fontSize: 10, color: "var(--invict-text-muted)", marginTop: 3 }}>{q.attempts_count} intentos · {q.passed_count} aprobados</div>
            </div>
            <button onClick={() => setEditing(q)} className="btn-secondary" style={{ padding: "8px 12px", fontSize: 11, display: "inline-flex", alignItems: "center", gap: 5 }} data-testid={`quiz-edit-${q.id}`}><Pencil size={13}/> Editar</button>
            <button onClick={() => setAuditFor(q)} className="btn-secondary" style={{ padding: "8px 12px", fontSize: 11, display: "inline-flex", alignItems: "center", gap: 5 }} data-testid={`quiz-audit-${q.id}`}><BarChart3 size={13}/> Auditoría</button>
            {isAdmin && <button onClick={() => remove(q.id)} style={{ background: "none", border: "none", color: "#BF3437", cursor: "pointer", padding: 6 }} data-testid={`quiz-del-${q.id}`}><Trash2 size={16}/></button>}
          </div>
        ))}
      </div>

      {creating && <QuizBuilder onClose={() => { setCreating(false); load(); }} />}
      {editing && <QuizBuilder existing={editing} onClose={() => { setEditing(null); load(); }} />}
      {auditFor && <QuizAudit quiz={auditFor} onClose={() => setAuditFor(null)} />}
    </div>
  );
}

function QuizBuilder({ onClose, existing = null }) {
  const isEdit = !!existing;
  const [meta, setMeta] = useState(existing
    ? { title: existing.title, description: existing.description || "", pass_score: existing.pass_score, period_days: existing.period_days }
    : { title: "", description: "", pass_score: 70, period_days: 30 });
  const [roles, setRoles] = useState(() => {
    const base = { admin: false, supervisor: false, vigilador: true, operario_limpieza: false, custodio: false };
    if (existing) {
      USER_ROLE_KEYS.forEach((r) => { base[r] = (existing.assign_roles || []).includes(r); });
    }
    return base;
  });
  const [questions, setQuestions] = useState(existing
    ? existing.questions.map((q) => ({ text: q.text, options: [...q.options], correct_index: q.correct_index }))
    : [{ text: "", options: ["", ""], correct_index: 0 }]);
  const [busy, setBusy] = useState(false);

  const addQ = () => setQuestions((qs) => [...qs, { text: "", options: ["", ""], correct_index: 0 }]);
  const rmQ = (i) => setQuestions((qs) => qs.filter((_, idx) => idx !== i));
  const updQ = (i, patch) => setQuestions((qs) => qs.map((q, idx) => idx === i ? { ...q, ...patch } : q));
  const updOpt = (qi, oi, val) => setQuestions((qs) => qs.map((q, idx) => idx === qi ? { ...q, options: q.options.map((o, j) => j === oi ? val : o) } : q));
  const addOpt = (qi) => setQuestions((qs) => qs.map((q, idx) => idx === qi ? { ...q, options: [...q.options, ""] } : q));
  const rmOpt = (qi, oi) => setQuestions((qs) => qs.map((q, idx) => idx === qi ? { ...q, options: q.options.filter((_, j) => j !== oi), correct_index: q.correct_index >= q.options.length - 1 ? 0 : q.correct_index } : q));

  const submit = async (e) => {
    e.preventDefault(); if (busy) return;
    const assign_roles = Object.keys(roles).filter((r) => roles[r]);
    if (assign_roles.length === 0) { toast.error("Asigná al menos un rol"); return; }
    for (const q of questions) {
      if (!q.text.trim()) { toast.error("Todas las preguntas necesitan texto"); return; }
      if (q.options.filter((o) => o.trim()).length < 2) { toast.error("Cada pregunta necesita al menos 2 opciones"); return; }
    }
    setBusy(true);
    try {
      const payload = {
        ...meta, pass_score: Number(meta.pass_score), period_days: Number(meta.period_days),
        assign_roles, questions: questions.map((q) => ({ text: q.text, options: q.options.filter((o) => o.trim()), correct_index: q.correct_index })),
      };
      if (isEdit) {
        await api.patch(`/quizzes/${existing.id}`, payload);
        toast.success("Cuestionario actualizado");
      } else {
        await api.post("/quizzes", payload);
        toast.success("Cuestionario creado");
      }
      onClose();
    } catch (e2) { toast.error(formatApiError(e2)); }
    finally { setBusy(false); }
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <form onSubmit={submit} style={{ background: "#FFFBF4", maxWidth: 680, width: "100%", maxHeight: "92vh", overflowY: "auto", borderRadius: 4 }} data-testid="quiz-builder">
        <div style={{ padding: "16px 20px", borderBottom: "2px solid #BF3437", display: "flex", justifyContent: "space-between", position: "sticky", top: 0, background: "#FFFBF4", zIndex: 2 }}>
          <h2 style={{ fontSize: 18, fontWeight: 900, margin: 0, color: "#0A0A0A" }}>{isEdit ? "Editar cuestionario" : "Nuevo cuestionario"}</h2>
          <button type="button" onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer" }}><X size={20}/></button>
        </div>
        {isEdit && <div style={{ padding: "8px 20px 0", fontSize: 11, color: "var(--invict-text-muted)" }}>Los intentos ya registrados no se modifican: quedan guardados tal como fueron respondidos.</div>}
        <div style={{ padding: 20 }}>
          <div className="field"><label>Título *</label><input required value={meta.title} onChange={(e) => setMeta({ ...meta, title: e.target.value })} data-testid="quiz-title"/></div>
          <div className="field"><label>Descripción</label><input value={meta.description} onChange={(e) => setMeta({ ...meta, description: e.target.value })} data-testid="quiz-desc"/></div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div className="field"><label>Puntaje mínimo (%)</label><input type="number" min="1" max="100" value={meta.pass_score} onChange={(e) => setMeta({ ...meta, pass_score: e.target.value })} data-testid="quiz-pass-score"/></div>
            <div className="field"><label>Repetir cada (días)</label><input type="number" min="1" value={meta.period_days} onChange={(e) => setMeta({ ...meta, period_days: e.target.value })} data-testid="quiz-period"/></div>
          </div>
          <div className="field"><label>Asignar a</label>
            <div style={{ display: "flex", gap: 10 }}>
              {USER_ROLE_KEYS.map((r) => (
                <label key={r} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, cursor: "pointer" }}>
                  <input type="checkbox" checked={roles[r]} onChange={(e) => setRoles({ ...roles, [r]: e.target.checked })} data-testid={`quiz-role-${r}`}/>{ROLE_LABEL[r]}
                </label>
              ))}
            </div>
          </div>

          <div style={{ fontSize: 11, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.1em", color: "#0A0A0A", margin: "16px 0 8px" }}>Preguntas</div>
          {questions.map((q, i) => (
            <div key={i} style={{ background: "#fff", border: "1px solid var(--invict-bone-3)", borderRadius: 8, padding: 12, marginBottom: 10 }} data-testid={`quiz-q-${i}`}>
              <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8 }}>
                <input value={q.text} onChange={(e) => updQ(i, { text: e.target.value })} placeholder={`Pregunta ${i + 1}`} style={{ flex: 1 }} data-testid={`quiz-q-text-${i}`}/>
                {questions.length > 1 && <button type="button" onClick={() => rmQ(i)} style={{ border: "none", background: "transparent", color: "#BF3437", cursor: "pointer" }}><Trash2 size={15}/></button>}
              </div>
              {q.options.map((opt, j) => (
                <div key={j} style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 5 }}>
                  <input type="radio" name={`correct-${i}`} checked={q.correct_index === j} onChange={() => updQ(i, { correct_index: j })} data-testid={`quiz-correct-${i}-${j}`} title="Marcar como correcta"/>
                  <input value={opt} onChange={(e) => updOpt(i, j, e.target.value)} placeholder={`Opción ${j + 1}`} style={{ flex: 1 }} data-testid={`quiz-opt-${i}-${j}`}/>
                  {q.options.length > 2 && <button type="button" onClick={() => rmOpt(i, j)} style={{ border: "none", background: "transparent", color: "var(--invict-text-muted)", cursor: "pointer" }}><X size={14}/></button>}
                </div>
              ))}
              <button type="button" onClick={() => addOpt(i)} style={{ fontSize: 11, fontWeight: 700, border: "1px dashed var(--invict-bone-3)", background: "#fff", borderRadius: 6, padding: "5px 10px", cursor: "pointer", marginTop: 4 }} data-testid={`quiz-add-opt-${i}`}>+ Opción</button>
              <div style={{ fontSize: 10, color: "var(--invict-text-muted)", marginTop: 6 }}>Marcá el círculo de la opción correcta.</div>
            </div>
          ))}
          <button type="button" onClick={addQ} className="btn-secondary" style={{ padding: "8px 14px", fontSize: 12 }} data-testid="quiz-add-question"><Plus size={13} style={{ display: "inline", marginRight: 4 }}/>Agregar pregunta</button>

          <button type="submit" disabled={busy} className="btn-primary" style={{ width: "100%", padding: 12, marginTop: 16 }} data-testid={isEdit ? "quiz-edit-submit" : "quiz-create-submit"}>{busy ? "Guardando..." : (isEdit ? "Guardar cambios" : "Crear cuestionario")}</button>
        </div>
      </form>
    </div>
  );
}

function QuizAudit({ quiz, onClose }) {
  const [attempts, setAttempts] = useState([]);
  useEffect(() => {
    api.get(`/quizzes/${quiz.id}/attempts`).then((r) => setAttempts(r.data)).catch(() => {});
  }, [quiz.id]);

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div style={{ background: "#FFFBF4", maxWidth: 680, width: "100%", maxHeight: "90vh", overflowY: "auto", borderRadius: 4 }} data-testid="quiz-audit-modal">
        <div style={{ padding: "16px 20px", borderBottom: "2px solid #BF3437", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ fontSize: 10, letterSpacing: "0.2em", color: "#BF3437", fontWeight: 800, textTransform: "uppercase" }}>Auditoría</div>
            <h2 style={{ fontSize: 18, fontWeight: 900, margin: 0, color: "#0A0A0A" }}>{quiz.title}</h2>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer" }}><X size={20}/></button>
        </div>
        <div style={{ padding: 16 }}>
          <div className="card" style={{ padding: 0, overflow: "hidden" }}>
            {attempts.length === 0 && <div style={{ padding: 20, textAlign: "center", fontSize: 12, color: "var(--invict-text-muted)" }}>Sin intentos registrados.</div>}
            {attempts.map((a) => (
              <div key={a.id} style={{ padding: "11px 14px", borderBottom: "1px solid var(--invict-bone-3)", display: "flex", gap: 10, alignItems: "center" }} data-testid={`attempt-${a.id}`}>
                {a.passed ? <CheckCircle2 size={18} color="#16a34a"/> : <XCircle size={18} color="#BF3437"/>}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 800, color: "#0A0A0A" }}>{a.user_name}{a.user_deleted && <DeletedUserBadge id={a.id} />} <span style={{ color: "var(--invict-text-muted)", fontWeight: 500, fontSize: 11 }}>· {ROLE_LABEL[a.user_role] || a.user_role}</span></div>
                  <div style={{ fontSize: 11, color: "var(--invict-text-muted)" }}>{a.correct}/{a.total} correctas · {new Date(a.submitted_at).toLocaleString("es-AR")} · IP {a.ip}</div>
                </div>
                <span style={{ fontWeight: 900, fontSize: 15, color: a.passed ? "#16a34a" : "#BF3437" }}>{a.score}%</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
